/**
 * BootstrapGate — blocking gate above the router. Fetches tenant bootstrap
 * (`GET /api/bootstrap`) once on mount so `authOrigin` / `loginUrl` exist
 * before AuthGate or any route (including `/auth/return`) renders.
 *
 * Any non-success is a blocking screen: no chrome, no AuthGate, no login
 * redirect. Never composes a host and never falls back to Vite env.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Alert, Box, Button } from '@mui/material';
import { ErrorDetails } from '@/components/ErrorDetails';
import { BootstrapError, fetchBootstrap, setBootstrap } from '@/config/bootstrap';
import { useCopy } from '@/hooks/useCopy';

type GateState = 'pending' | 'resolved' | 'failed';

export function BootstrapGate({ children }: { children: ReactNode }): ReactNode {
  const { t } = useCopy();
  const [state, setState] = useState<GateState>('pending');
  const [status, setStatus] = useState<number>(0);
  const [bootstrapError, setBootstrapError] = useState<unknown>(null);
  const started = useRef(false);

  const load = useCallback(async () => {
    setState('pending');
    setBootstrapError(null);
    try {
      const payload = await fetchBootstrap();
      setBootstrap(payload);
      setState('resolved');
    } catch (err) {
      setStatus(err instanceof BootstrapError ? err.status : 0);
      setBootstrapError(err);
      setState('failed');
    }
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void load();
  }, [load]);

  if (state === 'resolved') {
    return children;
  }

  if (state === 'failed') {
    const headline =
      status === 404
        ? t('bootstrap.errorNotRegistered', 'This host is not registered. Contact your administrator.')
        : t('bootstrap.error', 'Could not load application configuration.');
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: '0.5rem',
        }}
      >
        <Alert severity="error">
          {headline}
          <ErrorDetails error={bootstrapError} headline={headline} intent="read" />
        </Alert>
        <Button variant="outlined" onClick={() => void load()}>
          {t('common.retry', 'Retry')}
        </Button>
      </Box>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: '0.5rem',
      }}
    >
      <p>{t('app.loading', 'Loading…')}</p>
    </div>
  );
}
